/**
 * Single source of truth for `data-testid` values.
 *
 * This module is imported by BOTH production components and the Playwright page
 * objects under `e2e/pages`. That is deliberate: renaming an id becomes a compile
 * error instead of a silently broken test.
 *
 * ## When to add an id here
 *
 * Only when an element cannot be reached reliably by its accessible role, label,
 * placeholder, or visible text. In order of preference a page object should use:
 *
 *   1. `getByRole('button', { name: 'Submit' })` / `getByLabel('Symbol')`
 *   2. `getByTestId(testIds.…)`  ← this module
 *   3. Nothing else. CSS class selectors are banned (Tailwind classes churn).
 *
 * Good reasons to add an id: page roots used as "screen is loaded" anchors,
 * repeated regions with identical accessible names (grids, KPI tiles, tiles in a
 * strip), and nodes whose label text is expected to change.
 *
 * ## When removing or renaming
 *
 * Update the page object in the same commit. `npm run typecheck:e2e` will fail
 * otherwise, which is the whole point of centralising these strings.
 *
 * See `e2e/README.md` for the full selector policy.
 */
export const testIds = {
  /** Application shell: header, sidebar, and main content host. */
  shell: {
    /** Root of the authenticated shell. Used as the "we are logged in" anchor. */
    root: 'shell-root',
    /** Sidebar `<aside>` root. */
    sidebar: 'nav-sidebar',
    /** Opens the overlay navigation when it is not pinned. */
    navigationMenuButton: 'navigation-menu-button',
    /** Pins or unpins navigation when the shell is wide enough. */
    navigationPinButton: 'navigation-pin-button',
    /** Dismisses an open overlay navigation drawer. */
    navigationBackdrop: 'navigation-backdrop',
    /** Expand/collapse toggle for a navigation group, keyed by `NavGroup.id`. */
    navGroup: (groupId: string) => `nav-group-${groupId}`,
    /** Clickable navigation leaf, keyed by `NavLeaf.id`. */
    navLeaf: (leafId: string) => `nav-leaf-${leafId}`,
    /** Non-clickable "coming soon" node, keyed by `NavPlaceholderLeaf.id`. */
    navPlaceholder: (placeholderId: string) => `nav-placeholder-${placeholderId}`,
  },

  /** `/portfolios` — the search + hierarchy grid archetype. */
  portfolioSearch: {
    root: 'portfolio-search-page',
    /** Shown while the initial portfolio query is in flight. */
    loading: 'portfolio-search-loading',
    /** Shown when the query succeeded but produced no rows. */
    empty: 'portfolio-search-empty',
    /** Wrapper around the hierarchy grid; present only when there is at least one row. */
    results: 'portfolio-search-results',
    /** Pending Valuation / PL cells while background summaries fill. */
    valuing: 'portfolio-search-valuing',
  },

  /** `/portfolios/:id/wealth` — total, one card per held book, the cross-book strip, then each held book. */
  wealthNodeLanding: {
    root: 'wealth-node-landing',
    holdsNothing: 'wealth-node-landing-holds-nothing',
    total: 'wealth-node-landing-total',
    crossBook: 'wealth-node-landing-cross-book',
    book: (title: string) => `wealth-node-landing-book-${title.toLowerCase().replace(/\s+/g, '-')}`,
  },

  /** `/portfolios/:id` — book detail. */
  portfolioDetail: {
    /** Pending Valuation cells while account summaries fill. */
    valuing: 'portfolio-accounts-valuing',
  },

  /** `/custodians/:id` — custodian detail. */
  custodianDetail: {
    valuing: 'custodian-accounts-valuing',
  },

  /**
   * Portfolio hierarchy grid (Syncfusion). The id is applied inside the name-cell
   * template, so there is exactly one node per *data* row — header rows, which also
   * carry `role="row"`, are excluded. Each node's text content is the portfolio name.
   */
  portfolioGrid: {
    row: 'portfolio-row',
    /** Pulse bar in Valuation / PL while summaries are still in flight. */
    valuingCell: 'portfolio-valuation-pending',
  },

  /** `/instruments/new/equity` — the tabbed definition form archetype. */
  defineEquity: {
    root: 'define-equity-page',
  },

  /** Chrome shared by every instrument definition screen. */
  instrumentDefinition: {
    /** Banner rendered after a command is accepted by the API. */
    successBanner: 'instrument-definition-success',
    /** Banner rendered when the command POST fails. */
    errorBanner: 'instrument-definition-error',
  },

  /** `/deals/capture/buy` and siblings — the transactional capture archetype. */
  dealCapture: {
    root: 'deal-capture-page',
    /** Banner rendered after the capture command is accepted. */
    successBanner: 'deal-capture-success',
    /** Banner rendered when the capture command fails. */
    errorBanner: 'deal-capture-error',
  },

  /**
   * Shared chrome on every report dashboard (P&L, performance, transactions, …).
   * Holdings keeps `holdingsReport.root` / `holdingsReport.grid` so existing specs stay stable.
   */
  reportDashboard: {
    root: (dashboardId: string) => `report-dashboard-${dashboardId}`,
    /** Results region for the active tile (grid, ledger, or table — not a row count). */
    results: 'report-dashboard-results',
    chart: 'report-dashboard-chart',
    error: 'report-dashboard-error',
  },

  /** `/reports/dashboards/client-statement` — the HTML statement composer. */
  clientStatement: {
    root: 'client-statement-page',
    template: 'client-statement-template',
    panel: (key: string) => `client-statement-panel-${key}`,
    generate: 'client-statement-generate',
    lastResult: 'client-statement-last-result',
  },

  /** Coverage caption beside a figure (`WealthCoverage.Label` when `MustDisclose`). */
  wealthCoverage: {
    note: 'wealth-coverage-note',
    composerNotes: 'wealth-coverage-composer-notes',
  },

  /** `/reports/explorer` — the WPF Report Explorer catalog. */
  reportExplorer: {
    root: 'report-explorer-page',
    search: 'report-explorer-search',
  },

  /** `/reports/dashboards/holdings-positions` — the parameterised report archetype. */
  holdingsReport: {
    root: 'holdings-report-page',
    /**
     * Wrapper around the results grid for the active tile.
     *
     * Note for test authors: this anchors "the grid rendered", NOT a row count.
     * The holdings grid is AG Grid with row virtualisation, so the number of row
     * elements in the DOM reflects the viewport, not the dataset. Assert on the
     * KPI tiles for counts.
     */
    grid: 'holdings-report-grid',
    /** Visible only while a report query is in flight. */
    refreshing: 'holdings-report-refreshing',
  },

  /** Dashboard tile strip shared by the report dashboards. */
  dashboardTiles: {
    strip: 'dashboard-tile-strip',
    /** One per tile, keyed by the tile id (which is also the `?tile=` query value). */
    tile: (tileId: string) => `dashboard-tile-${tileId}`,
  },

  /** KPI tiles. Keyed by label so assertions never depend on DOM order. */
  kpi: {
    tile: (label: string) => `kpi-${slugifyLabel(label)}`,
  },

  /**
   * Hierarchical portfolio picker (WPF `PEPortfolioTreeSelectorView`).
   * Several rows share these ids; page objects filter by visible name / `data-kind`.
   */
  portfolioPicker: {
    row: 'portfolio-tree-row',
    icon: 'portfolio-tree-icon',
  },

  /** `/reports/dashboards/what-if` — structured-product cash-flow scenario dashboard. */
  whatIf: {
    root: 'what-if-page',
    chart: 'what-if-price-chart',
    holdings: 'what-if-holdings',
    holdingRow: (symbol: string | undefined) => `what-if-holding-${(symbol ?? 'unknown').toLowerCase()}`,
    cashLedger: 'what-if-cash-ledger',
    missingSeed: 'what-if-missing-seed',
    refreshing: 'what-if-refreshing',
    overrideCount: 'what-if-override-count',
    ephemeralBadge: 'what-if-ephemeral-badge',
    saveScenario: 'what-if-save-scenario',
    cancelScenario: 'what-if-cancel-scenario',
    uncommitted: 'what-if-uncommitted',
  },

  /** Validation summary shared by the form screens. */
  validation: {
    /** The page-level summary rendered above the form card. */
    summary: 'validation-summary',
    /**
     * The copy of the summary rendered inside a form's own "Validation" tab.
     * Definition screens show both at once, so they need separate ids to stay
     * individually addressable.
     */
    tabSummary: 'validation-tab-summary',
    /** One per message, inside either summary. */
    message: 'validation-message',
  },
} as const;

/**
 * Normalises a human label into a stable id fragment: lowercase, non-alphanumerics
 * collapsed to single dashes, no leading/trailing dashes.
 *
 * `"Unrealized G/L"` → `"unrealized-g-l"`, `"MV + accrued (rep.)"` → `"mv-accrued-rep"`.
 *
 * Exported so page objects derive the same id from the same label the UI renders,
 * rather than hardcoding the slug.
 */
export function slugifyLabel(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}
